import { registeredTabsHandlers } from '@/global/components/view-layouts/tabs-layout/common/registered-tabs-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { api } from '@/global/services/api'
import { makeFormData } from '../../helpers/formData'
import gridView from '@/global/components/grid/GridView.vue'
import vehicleCommentGridBlock from './VehicleCommentGridBlock.vue'
import vehicleDamageMixin from '../../mixins/vehicleDamageMixin'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import { getBooleanFromValue } from '@/global/utils/utils'

export default {
  data () {
    return {
      showDamagesTable: true,
      showDamagesDetails: false,
      damagesDetailsTitle: this.$t('fleet.damages'),
      damageEditState: false,
      damageCreateModeState: false,
      damageValidationErrors: {},
      showDamagesLoader: false,
      damagePermissions: {}
    }
  },

  mixins: [
    vehicleDamageMixin
  ],

  computed: {
    damagesConfig () {
      return {
        title: this.$t('fleet.damages'),
        key: 'damages',
        visible: getBooleanFromValue(this.permissions?.canReadVehicleDamage),
        validationErrors: this.damageValidationErrors,
        onTabChange: () => {
          this.showMainEditButtons = false
        },
        editUpdate: (event) => {
          const { edit } = event || {}
          this.damageEditState = !!edit
        },
        createModeUpdate: (event) => {
          const { createMode } = event || {}
          this.damageCreateModeState = !!createMode
        },
        tabContent: [
          // table view
          {
            component: this.tableGridComponent,
            visible: this.showDamagesTable,
            showLoader: this.showDamagesLoader,
            apiConfig: {
              get: {
                module: 'fleet',
                route: 'vehicle-damages',
                queryParams: {
                  vehicle_id: this.$route.params.id
                }
              }
            },
            rowDblClick: (event) => this.onShowDamageDetails(event),
            mobileConfig: {
              title: this.$t('fleet.damages')
            },
            title_and_back_config: {
              title: this.$t('fleet.damages')
            },
            search_and_buttons_config: {
              newClick: () => this.onNewDamage(),
              showNewBtn: getBooleanFromValue(this.damagePermissions?.canCreate)
            },
            gridConfig: {
              component: gridView,
              blockConfig: {
                component: vehicleCommentGridBlock,
                showDetails: () => {
                  this.showDamagesTable = false
                  this.showDamagesDetails = true
                },
                setDetailsTitle: (val) => {
                  this.damagesDetailsTitle = val
                }
              },
              useBorder: false
            },
            onDataReceivedWithParams: (event) => {
              this.damagePermissions = event?.data?.meta?.permissions || {}
            },
            tableConfig: {
              cellsConfig: {
                driver: {
                  custom_display_value: (item) => {
                    return item?.driver?.full_name || '-'
                  }
                },
                type: {
                  custom_display_value: (item) => {
                    return item?.type?.description || '-'
                  }
                },
                date: {
                  custom_display_value: (item) => {
                    return item?.date ? dateService.formatCustom(item.date, FORMATS.isoDatetime, FORMATS.date) : '-'
                  }
                }
              },
              headers: [
                {
                  label: this.$t('fleet/damages.driver'),
                  key: 'driver',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/damages.damage_date'),
                  key: 'date',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/damages.damage_type'),
                  key: 'type',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/damages.damage_cost'),
                  key: 'cost',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/damages.comment'),
                  key: 'note',
                  always_visible: true
                }
              ],
              actionsConfig: [
                showDetailsAction(this.$t, (event) => this.onShowDamageDetails(event), { visible: getBooleanFromValue(this.damagePermissions?.canRead) }),
                deleteAction(this.$t, (event) => this.onDamageDelete(event), { visible: getBooleanFromValue(this.damagePermissions?.canDelete) })
              ],
              paginationConfig: {
                perPageOptions: [50, 100]
              },
              filtersConfig: {
                fields: [
                  {
                    key: 'body',
                    label: this.$t('fleet/vehicle_comment.text'),
                    visible: true,
                    editable: true
                  }
                ]
              }
            }
          },
          // details view
          {
            component: this.tabActionsComponent,
            visible: this.showDamagesDetails,
            title: this.damagesDetailsTitle,
            showEditButtons: true,
            showEditBtn: getBooleanFromValue(this.damagePermissions?.canUpdate),
            showBackButton: {
              onGoBack: () => this.exitFromDamageDetails()
            },
            onCancelTabSave: () => this.exitFromDamageDetails(true),
            onSaveClick: () => this.onSaveDamage()
          },
          {
            component: this.columnsContentComponent,
            visible: this.showDamagesDetails,
            showBackButton: {
              useChangesNotSavedDialog: false
            },
            rows: [
              {
                disabled: !getBooleanFromValue(this.damagePermissions?.canUpdate),
                fields: [
                  {
                    key: 'person_id',
                    label: this.$t('fleet/damages.driver'),
                    type: 'persons-selection',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'date',
                    label: this.$t('fleet/damages.damage_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    disableFutureDates: true,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'type',
                    label: this.$t('fleet/damages.damage_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'base',
                      route: 'codebook/autocomplete',
                      queryParams: {
                        type: 'FLEET_INCIDENT_DAMAGE_TYPE'
                      },
                      custom_display_value: (item) => {
                        return item?.description || null
                      }
                    },
                    keepInput: (searchValue, data) => this.keepType(searchValue, data),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'cost',
                    label: this.$t('fleet/damages.damage_cost'),
                    type: 'text',
                    field_type: 'number',
                    maxDecimal: 2,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'files',
                    label: this.$t('satellite-tracking.uploaded_files'),
                    type: 'files_upload',
                    subtype: 'image',
                    src: '/img/icons/table-image-icon.svg',
                    accept: '/image*',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'files',
                        removeApiConfig: {
                          module: 'fleet',
                          route: 'vehicle-damages/remove-file/'
                        }
                      }, 'image', 'damages')
                    },
                    visible: this.damageEditState || this.damageCreateModeState,
                    editable: true
                  },
                  {
                    key: 'files',
                    label: this.$t('satellite-tracking.uploaded_files'),
                    type: 'files_preview',
                    subtype: 'image',
                    src: '/img/icons/table-image-icon.svg',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'files',
                        removeApiConfig: {
                          module: 'fleet',
                          route: 'vehicle-damages/remove-file/'
                        }
                      }, 'image', 'damages')
                    },
                    visible: !this.damageEditState && !this.damageCreateModeState,
                    editable: true
                  },
                  {
                    key: 'note',
                    label: this.$t('fleet/damages.comment'),
                    type: 'textarea',
                    visible: true,
                    editable: true
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  methods: {
    onNewDamage () {
      const { setCreateMode, clearData } = registeredTabsHandlers?.damages || {}

      if (typeof setCreateMode === 'function') {
        setCreateMode(true)
      }
      if (typeof clearData === 'function') {
        clearData()
      }

      this.damagesDetailsTitle = this.$t('fleet/damages.adding_new_damage')
      this.showDamagesTable = false
      this.showDamagesDetails = true
    },

    exitFromDamageDetails (canceling = false) {
      const { clearData, setCreateMode, setEditMode, getCreateModeState, cancelSave } = registeredTabsHandlers?.damages || {}
      let isCreateMode = false
      if (typeof getCreateModeState === 'function') {
        isCreateMode = getCreateModeState()
      }

      if (isCreateMode) {
        this.showDamagesTable = true
        this.showDamagesDetails = false

        if (typeof clearData === 'function') {
          clearData()
        }
        if (typeof setCreateMode === 'function') {
          setCreateMode(false)
        }
        if (typeof setEditMode === 'function') {
          setEditMode(false)
        }
      }
      else if (typeof cancelSave === 'function') {
        if (!canceling) {
          this.showDamagesTable = true
          this.showDamagesDetails = false
        }

        cancelSave()
      }
      this.damageValidationErrors = {}
    },

    keepType (searchValue, options) {
      let items = []
      // apply existing items
      if (options?.items?.length) {
        items = options.items
      }

      // append new one
      if (searchValue) {
        items = [
          ...items,
          ...[
            {
              id: searchValue,
              description: searchValue
            }
          ]
        ]
      }

      return {
        value: searchValue || null,
        items
      }
    },

    async onSaveDamage () {
      try {
        const { getFormData } = registeredTabsHandlers?.damages || {}
        const { fetchData } = registeredTableHandlers?.damages || {}
        const { id: vehicleId } = this.$route?.params || {}

        if (!vehicleId) return

        const data = typeof getFormData === 'function' ? getFormData()?.data : {}

        if (data?.date) {
          data.date = dateService.formatCustom(data.date, FORMATS.date, FORMATS.isoDate)
        }
        data.person_id = data.person_id?.[0]?.id || data.person_id?.[0]

        const formData = makeFormData(data,
          ['files'],
          {
            vehicle_id: vehicleId
          }
        )

        let apiUrl = 'vehicle-damages'
        const { id: commentId } = data || {}
        if (!this.damageCreateModeState && commentId) {
          formData.append('_method', 'PATCH')
          apiUrl += `/${commentId}`
        }

        await api('fleet').post(apiUrl, formData)
        this.exitFromDamageDetails()

        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        this.damageValidationErrors = exception?.response?.data?.errors || {}
        console.error(exception)
      }
    }
  }
}
